import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  CheckCircle2, 
  Edit3, 
  Save, 
  X,
  Sparkles,
  Eye,
  Trash2
} from 'lucide-react';
import { getQADrafts, createOrUpdateQA, publishQA } from '../../lib/apiClient';

export default function QAManager({ episodeId, projectId, insightId }) {
  const [qaEntries, setQaEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ question: '', answer: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!episodeId || !projectId) return;
    loadQAEntries();
  }, [episodeId, projectId]);

  async function loadQAEntries() {
    try {
      setLoading(true);
      const res = await getQADrafts(episodeId, { projectId });
      const list = res && typeof res === 'object' && 'data' in res ? res.data : res;
      setQaEntries(list || []);
    } catch (e) {
      console.error('Failed to load Q&A entries', e);
    } finally {
      setLoading(false);
    }
  }

  function startEdit(qa) {
    setEditingId(qa.id);
    setEditForm({
      question: qa.question,
      answer: qa.answer,
      intentType: qa.intent_type
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({ question: '', answer: '', intentType: 'other' });
  }

  async function handleSave(qa) {
    try {
      setSaving(true);
      await createOrUpdateQA({
        id: qa.id,
        projectId,
        episodeId,
        question: editForm.question,
        answer: editForm.answer,
        intentType: editForm.intentType
      });
      await loadQAEntries();
      cancelEdit();
    } catch (e) {
      console.error('Failed to save Q&A', e);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(qa) {
    if (!window.confirm('Publish this Q&A pair? It will be visible on the public topic page.')) return;
    try {
      await publishQA(qa.id, { projectId, episodeId });
      await loadQAEntries();
    } catch (e) {
      console.error('Failed to publish Q&A', e);
      alert('Failed to publish');
    }
  }

  async function handleDelete(qa) {
    if (!window.confirm('Archive this Q&A pair?')) return;
    try {
      await createOrUpdateQA({
        id: qa.id,
        projectId,
        episodeId,
        status: 'archived'
      });
      await loadQAEntries();
    } catch (e) {
      console.error('Failed to archive Q&A', e);
      alert('Failed to archive');
    }
  }

  const filteredEntries = insightId
    ? qaEntries.filter((qa) => qa.insight_id === insightId)
    : qaEntries;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-slate-500 text-sm">Loading Q&A...</div>
      </div>
    );
  }

  if (qaEntries.length === 0) {
    return (
      <div className="p-6 text-center">
        <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No Q&A pairs generated yet.</p>
        <p className="text-xs text-slate-600 mt-1">Q&A will be auto-generated during insight extraction.</p>
      </div>
    );
  }

  if (insightId && filteredEntries.length === 0) {
    return (
      <div className="p-6 text-center">
        <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No Q&A pairs for this insight yet.</p>
        <p className="text-xs text-slate-600 mt-1">Q&A is generated per insight. Try publishing or regenerating insights to populate this view.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div className="mb-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
          Q&A Pairs ({filteredEntries.length}{insightId ? '' : ` / ${qaEntries.length} episode-wide`})
        </h3>
        <p className="text-xs text-slate-600">
          Review and publish AI-generated questions & answers
        </p>
      </div>

      {filteredEntries.map((qa) => (
        <div 
          key={qa.id} 
          className={`border rounded-lg transition-all ${
            qa.status === 'published' 
              ? 'bg-green-500/5 border-green-500/20' 
              : 'bg-white/5 border-white/10 hover:border-primary/30'
          }`}
        >
          {editingId === qa.id ? (
            // Edit Mode
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Question</label>
                <input
                  type="text"
                  value={editForm.question}
                  onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Answer</label>
                <textarea
                  value={editForm.answer}
                  onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })}
                  rows={4}
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Intent Type</label>
                <select
                  value={editForm.intentType}
                  onChange={(e) => setEditForm({ ...editForm, intentType: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
                >
                  <option value="what">What (Definition)</option>
                  <option value="how">How (Process)</option>
                  <option value="why">Why (Reasoning)</option>
                  <option value="best">Best Practice</option>
                  <option value="compare">Comparison</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => handleSave(qa)}
                  disabled={saving}
                  className="flex-1 px-3 py-2 bg-primary hover:bg-primary-hover text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-3 py-2 bg-white/5 hover:bg-white/10 text-slate-400 text-sm rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      qa.status === 'published' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {qa.status}
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary">
                      {qa.intent_type}
                    </span>
                  </div>
                  <h4 className="font-semibold text-white text-sm mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{qa.question}</span>
                  </h4>
                  <p className="text-sm text-slate-400 leading-relaxed pl-6">
                    {qa.answer}
                  </p>
                </div>
              </div>

              {/* Metadata */}
              {qa.topics && (
                <div className="pl-6 mb-3">
                  <span className="text-xs text-slate-600">
                    Topic: <span className="text-primary">{qa.topics.title}</span>
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pl-6">
                <button
                  onClick={() => startEdit(qa)}
                  className="px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-white/5 rounded transition-colors flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" />
                  Edit
                </button>
                {qa.status === 'draft' && (
                  <button
                    onClick={() => handlePublish(qa)}
                    className="px-2 py-1 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded transition-colors flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Publish
                  </button>
                )}
                <button
                  onClick={() => handleDelete(qa)}
                  className="px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Archive
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
